import React, { useState } from 'react';
import { useStoreClubAuth } from '../../contexts/StoreClubAuthContext';
import { memberAPI } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';
import LauraChat from '../../components/StoreClub/LauraChat';

const ClubProfile: React.FC = () => {
  const { member, refreshProfile, logout } = useStoreClubAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: member?.name || '',
    email: member?.email || '',
    phone: member?.phone || '',
    street_address: member?.street_address || '',
    city: member?.city || '',
    state: member?.state || '',
    zip_code: member?.zip_code || '',
    subscribe_to_reminders: member?.subscribe_to_reminders ?? true,
    subscribe_to_weather_alerts: member?.subscribe_to_weather_alerts ?? false,
  });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await memberAPI.updateProfile({
        name: formData.name,
        phone: formData.phone,
        street_address: formData.street_address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        subscribe_to_reminders: formData.subscribe_to_reminders,
        subscribe_to_weather_alerts: formData.subscribe_to_weather_alerts,
      });
      await refreshProfile();
      setEditing(false);
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout();
      window.location.href = '/club/login';
    }
  };

  if (!member) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading profile...</div>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout>
      <LauraChat message={`Hi ${member.name.split(' ')[0]}! Here you can manage your account settings and preferences.`} />

      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.heading}>My Profile</h2>
          {!editing && (
            <button style={styles.editButton} onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>

        {error && (
          <div style={styles.error}>{error}</div>
        )}

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Account Information</h3>

          <div style={styles.field}>
            <label style={styles.label}>Name</label>
            {editing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={styles.input}
              />
            ) : (
              <div style={styles.value}>{member.name}</div>
            )}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <div style={styles.value}>{member.email}</div>
            {editing && <span style={styles.hint}>Email cannot be changed</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Phone</label>
            {editing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                style={styles.input}
                placeholder="(509) 555-1234"
              />
            ) : (
              <div style={styles.value}>{member.phone || 'Not provided'}</div>
            )}
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Delivery Address</h3>

          <div style={styles.field}>
            <label style={styles.label}>Street Address</label>
            {editing ? (
              <input
                type="text"
                value={formData.street_address}
                onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                style={styles.input}
              />
            ) : (
              <div style={styles.value}>{member.street_address || 'Not provided'}</div>
            )}
          </div>

          <div style={styles.fieldRow}>
            <div style={styles.fieldHalf}>
              <label style={styles.label}>City</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  style={styles.input}
                />
              ) : (
                <div style={styles.value}>{member.city || 'Not provided'}</div>
              )}
            </div>
            <div style={styles.fieldSmall}>
              <label style={styles.label}>State</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  style={styles.input}
                  maxLength={2}
                />
              ) : (
                <div style={styles.value}>{member.state || 'WA'}</div>
              )}
            </div>
            <div style={styles.fieldSmall}>
              <label style={styles.label}>ZIP</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  style={styles.input}
                  maxLength={10}
                />
              ) : (
                <div style={styles.value}>{member.zip_code || 'Not provided'}</div>
              )}
            </div>
          </div>

          {member.distance_miles && (
            <div style={styles.distance}>
              <span style={styles.distanceIcon}>📍</span>
              <span>{Number(member.distance_miles).toFixed(1)} miles from the store</span>
              {member.drive_time_minutes && (
                <span> (~{member.drive_time_minutes} min drive)</span>
              )}
            </div>
          )}
        </div>

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Notifications</h3>

          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={formData.subscribe_to_reminders}
              onChange={(e) => setFormData({ ...formData, subscribe_to_reminders: e.target.checked })}
              disabled={!editing}
            />
            <span style={styles.toggleLabel}>Feed Reminders</span>
            <span style={styles.toggleDesc}>Get notified when it's time to reorder</span>
          </label>

          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={formData.subscribe_to_weather_alerts}
              onChange={(e) => setFormData({ ...formData, subscribe_to_weather_alerts: e.target.checked })}
              disabled={!editing}
            />
            <span style={styles.toggleLabel}>Weather Alerts</span>
            <span style={styles.toggleDesc}>Get alerts for weather that affects your animals</span>
          </label>
        </div>

        {editing && (
          <div style={styles.actions}>
            <button
              style={styles.cancelButton}
              onClick={() => {
                setEditing(false);
                setFormData({
                  name: member?.name || '',
                  email: member?.email || '',
                  phone: member?.phone || '',
                  street_address: member?.street_address || '',
                  city: member?.city || '',
                  state: member?.state || '',
                  zip_code: member?.zip_code || '',
                  subscribe_to_reminders: member?.subscribe_to_reminders ?? true,
                  subscribe_to_weather_alerts: member?.subscribe_to_weather_alerts ?? false,
                });
              }}
            >
              Cancel
            </button>
            <button
              style={{
                ...styles.saveButton,
                opacity: saving ? 0.6 : 1,
              }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Account</h3>
          <button style={styles.logoutButton} onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '5rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  editButton: {
    backgroundColor: 'transparent',
    color: '#508590',
    border: '2px solid #508590',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  card: {
    backgroundColor: '#fff',
    padding: '1.25rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 1rem 0',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #e0e0e0',
  },
  field: {
    marginBottom: '1rem',
  },
  fieldRow: {
    display: 'flex',
    gap: '0.75rem',
  },
  fieldHalf: {
    flex: 2,
    marginBottom: '1rem',
  },
  fieldSmall: {
    flex: 1,
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#666',
    marginBottom: '0.25rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  value: {
    fontSize: '1rem',
    color: '#333',
  },
  hint: {
    fontSize: '0.75rem',
    color: '#999',
    fontStyle: 'italic',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  distance: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: '#508590',
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f0f7f8',
    borderRadius: '6px',
  },
  distanceIcon: {
    fontSize: '1rem',
  },
  toggle: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem 0',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
  },
  toggleLabel: {
    fontWeight: 500,
    color: '#333',
    flex: 1,
  },
  toggleDesc: {
    fontSize: '0.8125rem',
    color: '#666',
    flex: 2,
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveButton: {
    flex: 2,
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  logoutButton: {
    width: '100%',
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: 'none',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default ClubProfile;
